import { navigate } from "@reach/router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { Documentation } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Documentation";
import { saveDraftRFQ } from "actions/RFQ/rfqsAction";
import { ProcurerQuoteTab } from "./components/ProcurerQuoteTab";
import { Suppliers } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers";
import { PreviewRFQ } from "../../../../pages/RFQ/RFQ/CreateRFQ/PreviewRFQ";
import {
	formValuesGenerator,
} from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/createRFQUtils";
import TextButton from "newui/components/Button/TextButton";
import SubmitRFQModal from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/SubmitRFQModal";
import { showToast } from "utils/helpers";
import InvitePreApprovalDrawer from "components/Pages/RFQDetail/InvitePreApprovalDrawer/InvitePreApprovalDrawer";
import { RFX_STATUS } from "utils/constants";
import { logErrorIfDebug } from "utils/logging";
import ReturnableSchedule from "./components/ReturnableSchedule/ReturnableSchedule";
import { processFormValuesForSaving } from "./data-processor";
import ProjectDetail from "./components/ProjectDetail";
import RfxCreationLayout from "./components/RfxCreationLayout";
import { useRfxSubmissionData } from "../data-provider/RfxSubmissionDataProvider";
import { isRfxStatusMatch } from "../utils";
import { useCurrentCompany } from "hooks/auth";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useRfxCreationForm } from "./hooks/useRfxCreationForm";
import { useIsReadOnlyMode } from "newui/features/ExecutiveView";

const calculateCompletedSteps = (formValues, isQuoteTabValid) => {
	return [
		// Rfx Details
		formValues?.type !== "" &&
			formValues?.address?.value !== "" &&
			formValues?.deadline_date !== "" &&
			formValues?.deadline_time !== ""
			? 1
			: null,
		// Rfx Documents
		formValues?.documents?.length > 0 ? 2 : null,
		// Returnable Schedules
		formValues?.sections?.filter((val) => val?.name !== "").length > 0
			? 3
			: formValues?.returnable_schedule_file
				? 3
				: null,
		// Quote Tab
		isQuoteTabValid && 4,
		// Supplier List
		formValues?.guest?.length > 0 || formValues?.suppliers?.length > 0
			? 5
			: null,
	].filter((value) => value !== null);
};

const CreateRFQPage = () => {
	const { rfxData, rfxId, updateLocalRfxData } = useRfxSubmissionData();
	const company = useCurrentCompany();
	const company_id = company?.id;
	const { clientPaths } = useAuthContext();

	const formikRefLocal = useRef();
	const isReadOnly = useIsReadOnlyMode();

	const onFormInitialize = useCallback((formValues) => {
		if (
			rfxData?.status &&
			rfxData?.status.toUpperCase() === RFX_STATUS.EVALUATION
		) {
			navigate(clientPaths.rfx.all);
			return false;
		}
		formikRefLocal?.current?.setValues(formValues);
		return true;
	}, [rfxData?.status, clientPaths]);

	const {
		selectedTab,
		formikRef,
		isSubmittingForm,
		setIsSubmittingForm,
		isValidForm,
		setIsValidForm,
		rfxTypes,
		documentCategories,
		isNotFound,
		completedSteps,
		touchedTabs,
		setTouchedTabs,
		openModal,
		setOpenModal,
		tabRef,
		staticOptionsLoaded,
		isFileUploading,
		tabButtonList,
		activeTab,
		lastStep,
		enableOnChangeFormik,
		changeTab,
		onBackButtonClicked,
		resetRfxData,
		makeHandleTabChangeWithSave,
		makeHandleSubmitOutside,
	} = useRfxCreationForm({
		isTemplate: false,
		lastStepTab: 5,
		forceSaveTabs: [1, 2, 4],
		rfxData,
		updateLocalRfxData,
		companyId: company_id,
		calculateCompletedSteps,
		onFormInitialize,
	});

	// Sync local ref with hook ref (onFormInitialize needs it before hook returns)
	formikRefLocal.current = formikRef.current;

	// ---- RFQ-specific: quoteTabRef for flushing edits before preview ----
	const quoteTabRef = useRef(null);
	const { onOpen: onPreviewOpen, onClose, isOpen } = useDisclosure();
	const onOpen = useCallback(async () => {
		await quoteTabRef.current?.saveQuoteTab?.();
		onPreviewOpen();
	}, [onPreviewOpen]);
	const {
		onOpen: onApprovalOpen,
		onClose: onApprovalClose,
		isOpen: isApprovalOpen,
	} = useDisclosure();

	// ---- RFQ-specific: redirect if not draft ----
	useEffect(() => {
		if (!rfxData) {
			return;
		}
		if (!isRfxStatusMatch(rfxData.status, RFX_STATUS.DRAFT)) {
			navigate(clientPaths.viewRfx(rfxData, "all"));
		}
	}, [clientPaths, rfxData]);

	// ---- RFQ-specific: save handler ----
	const handleSave = useCallback(
		async (additional_message, type) => {
			const processContinueButton = (type, invitationSentEmails = []) => {
				switch (type) {
					case "next":
						const currentIndex = tabButtonList.findIndex(
							(item) => item.value === selectedTab
						);
						const nextIndex = currentIndex + 1;
						if (tabButtonList?.length > nextIndex) {
							changeTab(tabButtonList[nextIndex]);
						}
						break;
					case "submit":
						// The confirmation modal renders over the destination view, so
						// the recipients travel with the navigation rather than blocking it.
						navigate(clientPaths.viewRfx(rfxData, "all"), {
							state: invitationSentEmails.length
								? { invitationSentEmails }
								: undefined,
						});
						break;
					default:
						break;
				}
			};

			// Read-only (executive switched) mode: never save or validate.
			if (isReadOnly) {
				return false;
			}

			setIsSubmittingForm(true);

			// Quote tab has own save mechanism
			if (selectedTab === 4) {
				const saveResult = await quoteTabRef.current?.saveQuoteTab?.();
				if (saveResult) {
					showToast("Quote progress saved successfully", "Save Successful", true);
					processContinueButton(type);
				}
				setIsSubmittingForm(false);
				return saveResult;
			}

			try {
				const values = formikRef?.current?.values;

				const submissionFormat = processFormValuesForSaving(
					additional_message,
					null,
					values
				);

				Object.keys(submissionFormat).forEach((key) => {
					if (submissionFormat[key] === undefined) {
						delete submissionFormat[key];
					}
				});

				const isSubmitAction = Boolean(lastStep && openModal);

				const res = await saveDraftRFQ(
					rfxId,
					submissionFormat,
					selectedTab,
					isSubmitAction
				);

				if (res) {
					resetRfxData(res);
					// DO NOT "harden" this into a delta. On a genuine first send the
					// delta is EMPTY, so `invitationSentEmails.length` is 0, navigation
					// carries no state, and the confirmation modal never opens — the
					// feature deletes itself. Verify via RfxService::saveRfqStep5
					// before trusting this comment.
					//
					// The full `supplierInvited.all` is the CORRECT payload, not a
					// tolerable one: saveRfqStep5 creates the invite rows (status
					// INVITED, guests with sendEmails:false) during ordinary DRAFT
					// SAVES, and submit is what actually emails them. So the rows that
					// exist here are precisely the invitations this submit dispatches —
					// `.all` means "everyone being invited right now".
					//
					// This is therefore NOT the invite-supplier drawer's situation. The
					// surfaces differ in kind: there, `.all` really is cumulative
					// history and must be diffed against a pre-request snapshot; here it
					// is the pending set. Same field, different meaning.
					//
					// What keeps it that way is that the RFx is necessarily DRAFT on
					// this path: `"submit"` only originates from SubmitRFQModal, which
					// needs tab 5 plus an open modal (structural), and a non-DRAFT RFx
					// is redirected away at :130-138 — a client-side useEffect, NOT a
					// route guard, so that half is a soft backstop.
					const invitationSentEmails = isSubmitAction
						? (res?.supplierInvited?.all ?? [])
								.map((invited) => invited?.email)
								.filter(Boolean)
						: [];
					processContinueButton(type, invitationSentEmails);
				} else {
					formikRef?.current?.submitForm();
					lastStep && openModal && setTouchedTabs([1, 2, 3, 4, 5]);
					setOpenModal(false);
				}
				return true;
			} catch (e) {
				logErrorIfDebug("Error during handleSave:", e);
				setIsSubmittingForm(false);
				return false;
			} finally {
				setIsSubmittingForm(false);
			}
		},
		[
			isReadOnly,
			clientPaths,
			lastStep,
			openModal,
			rfxId,
			rfxData,
			selectedTab,
			resetRfxData,
			tabButtonList,
			formikRef,
			changeTab,
			setIsSubmittingForm,
			setTouchedTabs,
			setOpenModal,
		]
	);

	// Wire page-specific handleSave into the shared hook's factory functions
	const handleTabChangeWithSave = useMemo(
		() => makeHandleTabChangeWithSave(handleSave),
		[makeHandleTabChangeWithSave, handleSave]
	);

	const handleSubmitOutside = useMemo(
		() => makeHandleSubmitOutside(handleSave, () => setOpenModal(true)),
		[makeHandleSubmitOutside, handleSave, setOpenModal]
	);

	const onRfxTypeChange = (e) => {
		const type = e ? rfxTypes.find((type) => type.code === e.value) : null;
		const rfxQuoteType = type?.show_quote ? rfxData?.quoteType ?? 'form' : null;
		updateLocalRfxData({ ...rfxData, rfxType: type, quoteType: rfxQuoteType });
	};

	const hasLoaded = staticOptionsLoaded && rfxData;

	const blockSendingRFx = lastStep
		? !isValidForm || isSubmittingForm || isFileUploading || (rfxData?.rfxType?.show_quote && !rfxData?.isQuoteTabValid)
		: isSubmittingForm || isFileUploading;

	return (
		<RfxCreationLayout
			isNotFound={isNotFound}
			selectedTab={selectedTab}
			formikRef={formikRef}
			isSubmittingForm={isSubmittingForm}
			isValidForm={isValidForm}
			setIsValidForm={setIsValidForm}
			completedSteps={completedSteps}
			touchedTabs={touchedTabs}
			tabRef={tabRef}
			isFileUploading={isFileUploading}
			tabButtonList={tabButtonList}
			lastStep={lastStep}
			enableOnChangeFormik={enableOnChangeFormik}
			onBackButtonClicked={onBackButtonClicked}
			handleTabChangeWithSave={handleTabChangeWithSave}
			handleSubmitOutside={handleSubmitOutside}
			isTemplate={false}
			isEdit={true}
			hasLoaded={hasLoaded}
			initialBlockSendingRFx={blockSendingRFx}
			rfxData={rfxData}
			companyId={company_id}
			headerProps={{
				isTemplateQuoteFormInvalid: false,
				loadRFQData: rfxData,
				onOpen: onOpen,
				onOpenApproval: onApprovalOpen,
			}}
			renderTabContent={({ setFieldError, setFieldValue }) => (
				<>
					{selectedTab === 1 && (
						<ProjectDetail
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							rfxData={rfxData}
							rfxTypes={rfxTypes}
							onRfxTypeChange={onRfxTypeChange}
						/>
					)}
					{selectedTab === 2 && (
						<Documentation
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							documentCategories={documentCategories}
							isTemplate={false}
							rfxOrRfxTemplateId={rfxId}
						/>
					)}
					{selectedTab === 3 && (
						<ReturnableSchedule
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							setFieldError={setFieldError}
							setFieldValue={setFieldValue}
							isTemplate={false}
							rfxOrRfxTemplateId={rfxId}
							previewRfxHandler={onOpen}
						/>
					)}
					{selectedTab === 4 && (
						<ProcurerQuoteTab
							ref={quoteTabRef}
							resourceType={"rfx"}
							subTitlePage={activeTab.subTitle}
							titlePage={activeTab.title}
							previewRfxHandler={onOpen}
						/>
					)}
					{lastStep && (
						<Suppliers rfqId={rfxId} showErrorMessage={false} />
					)}
				</>
			)}
			renderFooterPrefix={() => (
				<TextButton
					disabled={isSubmittingForm || isFileUploading}
					onClick={() => handleSubmitOutside("save")}
				>
					Save Draft
				</TextButton>
			)}
			renderAfterForm={() => (
				<>
					{isOpen && (
						<PreviewRFQ
							isOpen={isOpen}
							onClose={onClose}
							rfqData={rfxData}
							documentCategories={documentCategories}
							rfqTypes={rfxTypes}
							isTemplate={false}
							previewTab={activeTab.previewTab}
						/>
					)}
					<SubmitRFQModal
						isOpen={openModal}
						setIsOpen={setOpenModal}
						rfqId={rfxId}
						rfqDetail={rfxData}
						isSaving={isSubmittingForm}
						companyName={company?.name}
						handleSubmit={handleSubmitOutside}
					/>
				</>
			)}
			renderAfterContent={() => (
				<InvitePreApprovalDrawer
					rfqName={rfxData?.name}
					onClose={onApprovalClose}
					isOpen={isApprovalOpen}
					rfx={rfxData}
				/>
			)}
		/>
	);
};

export default CreateRFQPage;
